import type { NextPage, GetServerSideProps } from 'next'
import { ParsedUrlQuery } from 'querystring'
import ReactMarkdown from 'react-markdown'
import Image from 'next/legacy/image'
import {
  PageTemplate,
  RecommendedDocuments,
  Layout,
  ButtonPrimary
} from '../../components'
import {
  PageConfig,
  PageComponentProps,
  RecommendedDocumentsProps
} from '../../types/page_component_types'
import { getPagePropsBySlug } from '../../utils/get_page_props_by_slug'
import { Locale } from '../../types/shared_types'

interface VenturePageContent {
  slug: string
  meta: {
    title: string
    description: string
  }
  venture_id: string
  logo: {
    alt: string
    src: string
  }
  heroImage: {
    alt: string
    src: string
  }
  shortDescription: string
  homepage: string
  forum: boolean
  content: PageComponentProps[]
}

const VenturePage: NextPage<{
  pageContent: VenturePageContent
  pageConfig: PageConfig
  recommendations: RecommendedDocumentsProps[]
  availableLocales?: string[]
}> = ({ pageContent, pageConfig, recommendations, availableLocales }) => {
  return (
    <Layout
      config={pageConfig}
      meta={pageContent?.meta}
      slug={pageContent?.slug}
      availableLocales={availableLocales}
    >
      <div className="mx-auto flex max-w-[70.5rem] flex-col">
        <div className="relative mx-auto mb-8 mt-0 h-[27.5rem] w-full pr-6 pl-6 md:mt-8 lg:pr-0 lg:pl-0">
          <Image
            src={pageContent?.heroImage?.src}
            alt={pageContent?.heroImage?.alt}
            layout="fill"
            objectFit="cover"
          />
          <div className="absolute left-20 bottom-16 flex flex-col items-center justify-center md:bottom-8 md:flex-row">
            <div className="clipImage relative flex h-32 w-32 items-center justify-center bg-white md:mr-8">
              <Image
                src={pageContent?.logo?.src}
                alt={pageContent?.logo?.alt}
                layout="fixed"
                width={100}
                height={100}
                objectFit="contain"
              />
            </div>
            <h1 className="text-white [text-shadow:0_4px_8px_rgba(0,0,0,0.6)]">
              {pageContent?.venture_id}
            </h1>
          </div>
        </div>
        <h2 className="mb-8 pr-6 pl-6 lg:pr-0 lg:pl-0">
          About {pageContent?.venture_id}
        </h2>

        <ReactMarkdown className="pr-6 pl-6 lg:pr-0 lg:pl-0">
          {pageContent?.shortDescription}
        </ReactMarkdown>

        {pageContent?.content && <PageTemplate {...pageContent} />}

        {!pageContent?.forum && pageContent?.homepage && (
          <ButtonPrimary
            className="mx-auto my-12"
            link={pageContent?.homepage}
            label={`${pageContent?.venture_id} homepage`}
          />
        )}
      </div>
      <RecommendedDocuments
        recommendations={recommendations}
        config={pageConfig.recommendedConfig}
      />
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await getPagePropsBySlug(context, '/ventures', {
    with_recommendations: true
  })
}

export default VenturePage
